// src/pages/surveys/SurveyPublic.js
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc, getDoc, getDocs, collection, addDoc, query, where, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { db, auth, googleProvider, storage } from '../../config/firebase';

// Minimal HTML sanitizer — keeps only safe inline tags (b, i, u, br, strong, em, span)
function sanitize(html) {
  if (!html) return '';
  // Remove script blocks entirely
  let out = html.replace(/<script[\s\S]*?<\/script>/gi, '');
  // Keep only allowed tags; strip everything else
  out = out.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g, (match, tag) => {
    const allowed = ['b', 'i', 'u', 'br', 'strong', 'em', 'span'];
    if (allowed.includes(tag.toLowerCase())) {
      // Return a clean version of the tag (no attributes except for span)
      const isClose = match.startsWith('</');
      return isClose ? `</${tag.toLowerCase()}>` : `<${tag.toLowerCase()}>`;
    }
    return '';
  });
  return out;
}

function RichText({ html, style, as: Tag = 'div' }) {
  if (!html) return null;
  return <Tag style={style} dangerouslySetInnerHTML={{ __html: sanitize(html) }} />;
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 16px 80px',
    fontFamily: 'var(--font-body)',
  },
  headerCard: {
    width: '100%',
    maxWidth: 640,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    marginBottom: 16,
  },
  headerBanner: { height: 12 },
  headerBody: {
    background: 'white',
    padding: '28px 32px',
    borderBottom: '4px solid',
    position: 'relative',
  },
  kaLogo: {
    position: 'absolute',
    top: 12,
    right: 24,
    width: 96,
    height: 96,
    objectFit: 'contain',
    opacity: 0.85,
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 26,
    fontWeight: 700,
    color: '#1a2744',
    marginBottom: 8,
  },
  description: { fontSize: 15, color: '#4d5f7a', lineHeight: 1.6 },
  card: {
    width: '100%',
    maxWidth: 640,
    background: 'white',
    borderRadius: 10,
    padding: '24px 28px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
    marginBottom: 12,
  },
  qLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a2744',
    marginBottom: 4,
    lineHeight: 1.4,
  },
  qDesc: { fontSize: 13, color: '#8d9db6', marginBottom: 12 },
  required: { color: '#ef4444', marginLeft: 3 },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e6ea',
    borderRadius: 7,
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#1a2744',
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e6ea',
    borderRadius: 7,
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 100,
    color: '#1a2744',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e6ea',
    borderRadius: 7,
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    outline: 'none',
    background: 'white',
    color: '#1a2744',
  },
  radioRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    cursor: 'pointer',
    fontSize: 14,
    color: '#1a2744',
  },
  ratingRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 8,
    justifyContent: 'center',
  },
  ratingBtn: {
    width: 44,
    height: 44,
    borderRadius: 8,
    border: '2px solid #e2e6ea',
    background: 'white',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    transition: 'all 0.15s',
  },
  submitBtn: {
    width: '100%',
    maxWidth: 640,
    padding: '14px',
    background: '#1a2744',
    color: 'white',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    marginTop: 8,
    transition: 'opacity 0.15s',
  },
  // Sticky progress bar wrapper
  progressBarWrap: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    background: 'white',
    borderBottom: '1px solid #e2e6ea',
    padding: '10px 16px',
    boxSizing: 'border-box',
    marginBottom: 0,
    // Pull it out of the centered column to span full width
    marginLeft: 'calc(-50vw + 50%)',
    marginRight: 'calc(-50vw + 50%)',
    width: '100vw',
  },
  progressBar: {
    width: '100%',
    maxWidth: 640,
    height: 6,
    background: '#e2e6ea',
    borderRadius: 3,
    overflow: 'hidden',
    margin: '0 auto',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  progressLabel: {
    textAlign: 'center',
    fontSize: 11,
    color: '#8d9db6',
    marginTop: 6,
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  sectionBreak: {
    width: '100%',
    maxWidth: 640,
    padding: '16px 0 8px',
  },
  sectionTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 20,
    fontWeight: 700,
    color: '#1a2744',
    borderTop: '2px solid #e2e6ea',
    paddingTop: 12,
    marginBottom: 4,
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 7,
    padding: '8px 12px',
    fontSize: 12,
    color: '#b91c1c',
    marginTop: 6,
  },
  closedMsg: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#4d5f7a',
  },
  successCard: {
    width: '100%',
    maxWidth: 640,
    background: 'white',
    borderRadius: 12,
    padding: '48px 32px',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  },
  successIcon: { fontSize: 56, marginBottom: 20 },
  successTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 24,
    fontWeight: 700,
    color: '#1a2744',
    marginBottom: 12,
  },
  successMsg: { fontSize: 15, color: '#4d5f7a', lineHeight: 1.6 },
  kaFooter: {
    marginTop: 32,
    fontSize: 12,
    color: '#8d9db6',
    textAlign: 'center',
  },
  fileUploadZone: {
    border: '2px dashed #e2e6ea',
    borderRadius: 10,
    padding: '28px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    background: '#fafbfc',
  },
  fileUploadInput: { display: 'none' },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#f0f4ff',
    border: '1px solid #c7d2fe',
    borderRadius: 6,
    padding: '5px 10px',
    fontSize: 13,
    color: '#3730a3',
    marginTop: 10,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  progressBar: {
    height: 4,
    background: '#e2e6ea',
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 10,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.2s',
  },
};

export default function SurveyPublic() {
  const { id } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [fileUploading, setFileUploading] = useState({}); // { [qId]: progress 0-100 | 'error' }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [authUser, setAuthUser] = useState(undefined);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState('');
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0); // which section page we're on

  useEffect(() => {
    getDoc(doc(db, 'surveys', id)).then(snap => {
      if (snap.exists()) setSurvey(snap.data());
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setAuthUser(u));
    return unsub;
  }, []);

  // Check Firestore receipt for auth users once both survey and user are known
  useEffect(() => {
    if (!survey || authUser === undefined || authUser === null) return;
    if (survey.settings.allowMultiple) return;

    const checkReceipt = async () => {
      try {
        const q = query(
          collection(db, 'surveyReceipts'),
          where('surveyId', '==', id),
          where('uid', '==', authUser.uid)
        );
        const snap = await getDocs(q);
        if (!snap.empty) setAlreadySubmitted(true);
      } catch (err) {
        // Fall back to localStorage if receipt check fails
        const key = `survey_submitted_${id}_${authUser.uid}`;
        if (localStorage.getItem(key)) setAlreadySubmitted(true);
      }
    };
    checkReceipt();
  }, [survey, authUser, id]);

  useEffect(() => {
    if (survey?.settings?.authRequired && authUser) {
      setRespondentName(authUser.displayName || '');
      setRespondentEmail(authUser.email || '');
    }
  }, [survey, authUser]);

  const setAnswer = (qId, val) => setAnswers(prev => ({ ...prev, [qId]: val }));

  const toggleCheckbox = (qId, opt) => {
    const current = answers[qId] || [];
    const updated = current.includes(opt)
      ? current.filter(o => o !== opt)
      : [...current, opt];
    setAnswer(qId, updated);
  };

  const handleFileUpload = async (qId, files, q) => {
    if (!files || files.length === 0) return;

    const maxBytes = (q.fileMaxMB || 10) * 1024 * 1024;
    const fileList = Array.from(files);

    // Validate size
    for (const file of fileList) {
      if (file.size > maxBytes) {
        setErrors(prev => ({ ...prev, [qId]: `File "${file.name}" exceeds the ${q.fileMaxMB || 10} MB limit.` }));
        return;
      }
    }
    setErrors(prev => { const e = { ...prev }; delete e[qId]; return e; });
    setFileUploading(prev => ({ ...prev, [qId]: 0 }));

    try {
      const uploadedUrls = [];
      for (const file of fileList) {
        const path = `survey-uploads/${id}/${qId}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, file);
          task.on('state_changed',
            snap => {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              setFileUploading(prev => ({ ...prev, [qId]: pct }));
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploadedUrls.push({ name: file.name, url });
              resolve();
            }
          );
        });
      }
      // Store as array of {name, url} objects; single-file answers still use array for consistency
      setAnswer(qId, q.fileMultiple ? uploadedUrls : uploadedUrls[0]);
      setFileUploading(prev => { const s = { ...prev }; delete s[qId]; return s; });
    } catch (err) {
      console.error('File upload error:', err);
      setFileUploading(prev => ({ ...prev, [qId]: 'error' }));
      setErrors(prev => ({ ...prev, [qId]: 'Upload failed. Please try again.' }));
    }
  };

  const handleSignIn = async () => {
    setSigningIn(true);
    setSignInError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setSignInError('Sign-in failed. Please try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  const validate = () => {
    const errs = {};
    const authRequired = survey.settings.authRequired;
    if (!authRequired) {
      if (survey.settings.collectName && survey.settings.nameRequired && !respondentName.trim()) {
        errs['_name'] = 'Name is required';
      }
      if (survey.settings.collectEmail && survey.settings.emailRequired && !respondentEmail.trim()) {
        errs['_email'] = 'Email is required';
      }
    }
    // Validate all questions across all pages the user has visited
    const allVisitedQuestions = resolvedSectionOrder.flatMap(pi => sectionPages[pi]?.questions || []);
    allVisitedQuestions.forEach(q => {
      if (q.type === 'section' || q.type === 'hyperlink') return;
      if (q.required) {
        const ans = answers[q.id];
        if (ans === undefined || ans === null || ans === '' ||
          (Array.isArray(ans) && ans.length === 0)) {
          errs[q.id] = 'This field is required';
        }
      }
      // Block submit while a file is uploading
      if (q.type === 'file_upload' && fileUploading[q.id] !== undefined && fileUploading[q.id] !== 'error') {
        errs[q.id] = 'File is still uploading, please wait…';
      }
    });
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const authRequired = survey.settings.authRequired;

    // Duplicate check for anonymous users (localStorage only)
    if (!survey.settings.allowMultiple && !authUser) {
      const key = `survey_submitted_${id}_anon`;
      if (localStorage.getItem(key)) {
        setErrors({ _submit: 'You have already submitted a response to this survey.' });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Re-check status before submitting (survey may have been closed while tab was open)
      const freshSnap = await getDoc(doc(db, 'surveys', id));
      if (freshSnap.data()?.status === 'closed') {
        setErrors({ _submit: 'This survey is no longer accepting responses.' });
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, 'surveys', id, 'responses'), {
        answers,
        respondentName: respondentName || null,
        respondentEmail: respondentEmail || null,
        respondentUid: authRequired && authUser ? authUser.uid : null,
        submittedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
      });

      // Write receipt for auth users
      if (!survey.settings.allowMultiple && authUser) {
        await addDoc(collection(db, 'surveyReceipts'), {
          surveyId: id,
          uid: authUser.uid,
          email: authUser.email,
          submittedAt: serverTimestamp(),
        });
        // Also set localStorage as a fast cache
        localStorage.setItem(`survey_submitted_${id}_${authUser.uid}`, '1');
      }

      // localStorage-only for anonymous
      if (!survey.settings.allowMultiple && !authUser) {
        localStorage.setItem(`survey_submitted_${id}_anon`, '1');
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setErrors({ _submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const accentColor = survey?.settings?.accentColor || '#1a2744';

  // ── Split questions into section-pages ───────────────────────────
  // Each "page" is { section: q|null, questions: q[] }.
  // The first page is everything before the first section break (section=null).
  const sectionPages = useMemo(() => {
    if (!survey) return [];
    const pages = [];
    let current = { section: null, questions: [] };
    for (const q of survey.questions) {
      if (q.type === 'section') {
        pages.push(current);
        current = { section: q, questions: [] };
      } else {
        current.questions.push(q);
      }
    }
    pages.push(current);
    // Drop empty leading page if the survey starts with a section immediately
    if (pages.length > 1 && pages[0].section === null && pages[0].questions.length === 0) {
      pages.shift();
    }
    return pages;
  }, [survey]);

  // ── Resolve which section page to show, honouring section-level logic ──
  // sectionOrder is the sequence of page indices the user has visited / will visit.
  // We resolve it fresh each render so changing an answer re-routes immediately.
  const resolvedSectionOrder = useMemo(() => {
    if (!sectionPages.length) return [];
    const order = [];
    const visitedPageIndices = new Set();
    let idx = 0;
    while (idx < sectionPages.length) {
      if (visitedPageIndices.has(idx)) break;
      visitedPageIndices.add(idx);
      order.push(idx);
      const page = sectionPages[idx];
      // Evaluate section-level logic rules (stored on the section question itself)
      if (page.section?.logic?.length) {
        let jumped = false;
        for (const rule of page.section.logic) {
          // rule.sourceQuestionId + rule.when + rule.goto
          const srcId = rule.sourceQuestionId;
          const srcQ = survey.questions.find(q => q.id === srcId);
          if (!srcQ) continue;
          const ans = answers[srcId];
          const ansArr = Array.isArray(ans) ? ans : (ans !== undefined && ans !== '' ? [String(ans)] : []);
          if (rule.when && ansArr.includes(String(rule.when))) {
            if (rule.goto === '__end__') { return order; }
            if (rule.goto && rule.goto !== '__next__') {
              const targetPageIdx = sectionPages.findIndex(p => p.section?.id === rule.goto);
              if (targetPageIdx !== -1 && !visitedPageIndices.has(targetPageIdx)) {
                idx = targetPageIdx; jumped = true; break;
              }
            }
          }
        }
        if (jumped) continue;
      }
      idx++;
    }
    return order;
  }, [sectionPages, survey, answers]);

  // The page the user is currently viewing
  const safeCurrentIdx = Math.min(currentSectionIdx, resolvedSectionOrder.length - 1);
  const currentPageIdx = resolvedSectionOrder[safeCurrentIdx] ?? 0;
  const currentPage = sectionPages[currentPageIdx] || { section: null, questions: [] };
  const isLastPage = safeCurrentIdx >= resolvedSectionOrder.length - 1;

  // ── Within-page question visibility (question-level skip logic) ──
  const visibleQuestions = useMemo(() => {
    const all = currentPage.questions;
    const result = [];
    const visitedIndices = new Set();
    let i = 0;
    while (i < all.length) {
      if (visitedIndices.has(i)) break;
      visitedIndices.add(i);
      const q = all[i];
      result.push(q);
      let jumped = false;
      if (q.logic && q.logic.length > 0) {
        const ans = answers[q.id];
        const ansArr = Array.isArray(ans) ? ans : (ans !== undefined && ans !== '' ? [String(ans)] : []);
        for (const rule of q.logic) {
          if (rule.when && ansArr.includes(String(rule.when))) {
            if (rule.goto === '__end__') { return result; }
            if (rule.goto && rule.goto !== '__next__') {
              const targetIdx = all.findIndex(x => x.id === rule.goto);
              if (targetIdx !== -1 && !visitedIndices.has(targetIdx)) {
                i = targetIdx; jumped = true; break;
              }
            }
          }
        }
      }
      if (!jumped) i++;
    }
    return result;
  }, [currentPage, answers]);

  // Progress across all pages
  const answeredCount = (() => {
    let n = 0;
    for (const pageIdx of resolvedSectionOrder) {
      for (const q of sectionPages[pageIdx]?.questions || []) {
        if (q.type !== 'section' && q.type !== 'hyperlink' && answers[q.id] !== undefined && answers[q.id] !== '') n++;
      }
    }
    return n;
  })();
  const totalQuestions = (() => {
    let n = 0;
    for (const pageIdx of resolvedSectionOrder) {
      for (const q of sectionPages[pageIdx]?.questions || []) {
        if (q.type !== 'section' && q.type !== 'hyperlink') n++;
      }
    }
    return n;
  })();
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  // ── Next-section handler ─────────────────────────────────────────
  const goNextSection = () => {
    // Validate required questions on current page
    const errs = {};
    for (const q of visibleQuestions) {
      if (q.type === 'hyperlink') continue;
      if (q.required) {
        const ans = answers[q.id];
        if (ans === undefined || ans === null || ans === '' || (Array.isArray(ans) && ans.length === 0)) {
          errs[q.id] = 'This field is required';
        }
      }
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setCurrentSectionIdx(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading || authUser === undefined) return (
    <div style={{ ...s.page, justifyContent: 'center' }}>
      <div style={{ color: '#8d9db6' }}>Loading survey…</div>
    </div>
  );

  if (!survey) return (
      <div style={{ ...s.page, justifyContent: 'center' }}>
        <div style={s.closedMsg}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>Survey Not Found</div>
          <div>This survey link may be invalid or has been removed.</div>
        </div>
      </div>
    );

  if (survey.status === 'closed') {
    const accent = survey.settings?.accentColor || '#1a2744';
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#f8fafc', padding: 24,
      }}>
        <div style={{
          maxWidth: 480, width: '100%', textAlign: 'center',
          background: 'white', borderRadius: 16, padding: '48px 32px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          borderTop: `6px solid ${accent}`,
        }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
          <h1 style={{
            fontFamily: 'var(--font-display, system-ui)', fontSize: 22,
            fontWeight: 700, color: accent, marginBottom: 10,
          }}>
            {survey.title || 'This Survey'}
          </h1>
          <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.6 }}>
            This form is no longer accepting responses.
          </p>
        </div>
      </div>
    );
  }

  if (survey.status === 'draft') return (
    <div style={{ ...s.page, justifyContent: 'center' }}>
      <div style={s.closedMsg}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>Not Yet Published</div>
        <div>This survey is not yet accepting responses.</div>
      </div>
    </div>
  );

  if (survey.settings.authRequired && !authUser) return (
    <div style={{ ...s.page, justifyContent: 'center' }}>
      <div style={s.headerCard}>
        <div style={{ ...s.headerBanner, background: accentColor }} />
        <div style={{ ...s.headerBody, borderBottomColor: accentColor }}>
          {survey.settings.showLogo !== false && (
            <img src="/kings_logo.png" alt="King's Academy" style={s.kaLogo} />
          )}
          <div style={{ ...s.title, paddingRight: survey.settings.showLogo !== false ? 112 : 0 }}>
            {survey.title}
          </div>
          {survey.description && (
            <RichText
              html={survey.description}
              style={{ ...s.description, paddingRight: survey.settings.showLogo !== false ? 112 : 0 }}
            />
          )}
        </div>
      </div>
      <div style={{ ...s.card, maxWidth: 640, textAlign: 'center', padding: '40px 32px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>
          Sign in to continue
        </div>
        <div style={{ fontSize: 14, color: '#4d5f7a', marginBottom: 28, lineHeight: 1.6 }}>
          This survey is restricted to King's Academy accounts.<br />
          Please sign in with your King's Google account to respond.
        </div>
        <button
          onClick={handleSignIn}
          disabled={signingIn}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 12,
            padding: '12px 24px', background: 'white',
            border: '1.5px solid #e2e6ea', borderRadius: 8,
            fontSize: 15, fontWeight: 600,
            cursor: signingIn ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)', color: '#1a2744',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            opacity: signingIn ? 0.7 : 1, transition: 'all 0.15s',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          {signingIn ? 'Signing in…' : 'Sign in with Google'}
        </button>
        {signInError && <div style={{ ...s.error, marginTop: 16, textAlign: 'left' }}>{signInError}</div>}
      </div>
      <div style={s.kaFooter}>Powered by King's Academy Faculty Tool Kit · Madaba, Jordan</div>
    </div>
  );

  // Already submitted screen (auth users — Firestore receipt found)
  if (alreadySubmitted) return (
    <div style={s.page}>
      <div style={s.successCard}>
        <div style={s.successIcon}>✅</div>
        <div style={s.successTitle}>Already Submitted</div>
        <div style={s.successMsg}>You've already submitted a response to this survey. Only one response per person is allowed.</div>
      </div>
      <div style={s.kaFooter}>Powered by King's Academy Faculty Tool Kit · Madaba, Jordan</div>
    </div>
  );

  if (submitted) return (
    <div style={s.page}>
      <div style={s.successCard}>
        <div style={s.successIcon}>✅</div>
        <div style={s.successTitle}>Response Submitted!</div>
        <div style={s.successMsg}>{survey.settings.confirmationMessage}</div>
        {survey.settings.allowMultiple && (
          <button
            style={{ ...s.submitBtn, marginTop: 24, maxWidth: 200, margin: '24px auto 0' }}
            onClick={() => { setSubmitted(false); setAnswers({}); setRespondentName(''); setRespondentEmail(''); }}
          >
            Submit Another
          </button>
        )}
      </div>
      <div style={s.kaFooter}>Powered by King's Academy Faculty Tool Kit · Madaba, Jordan</div>
    </div>
  );

  const authRequired = survey.settings.authRequired;

  return (
    <div style={s.page}>

      {/* Sticky progress bar */}
      {survey.settings.showProgressBar && (
        <div style={s.progressBarWrap}>
          <div style={s.progressBar}>
            <div style={{ ...s.progressFill, width: `${progress}%`, background: accentColor }} />
          </div>
          <div style={s.progressLabel}>{answeredCount} of {totalQuestions} answered</div>
        </div>
      )}

      <div style={s.headerCard}>
        <div style={{ ...s.headerBanner, background: accentColor }} />
        <div style={{ ...s.headerBody, borderBottomColor: accentColor }}>
          {survey.settings.showLogo !== false && (
            <img
              src="/kings_logo.png"
              alt="King's Academy"
              style={s.kaLogo}
            />
          )}
          <div style={{ ...s.title, paddingRight: survey.settings.showLogo !== false ? 112 : 0 }}>
            {survey.title}
          </div>
          {survey.description && (
            <RichText
              html={survey.description}
              style={{ ...s.description, paddingRight: survey.settings.showLogo !== false ? 112 : 0 }}
            />
          )}
        </div>
      </div>

      {authRequired ? (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {authUser.photoURL ? (
              <img src={authUser.photoURL} alt="" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: accentColor, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, flexShrink: 0,
              }}>
                {(authUser.displayName || authUser.email || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1a2744' }}>{authUser.displayName || 'Anonymous'}</div>
              <div style={{ fontSize: 13, color: '#8d9db6' }}>{authUser.email}</div>
            </div>
            <button
              onClick={() => auth.signOut()}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 12, color: '#8d9db6', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : (
        (survey.settings.collectName || survey.settings.collectEmail) && (
          <div style={s.card}>
            {survey.settings.collectName && (
              <div style={{ marginBottom: 16 }}>
                <div style={s.qLabel}>Your Name{survey.settings.nameRequired && <span style={s.required}>*</span>}</div>
                <input style={s.input} value={respondentName} onChange={e => setRespondentName(e.target.value)} placeholder="Full name" />
                {errors['_name'] && <div style={s.error}>{errors['_name']}</div>}
              </div>
            )}
            {survey.settings.collectEmail && (
              <div>
                <div style={s.qLabel}>Email Address{survey.settings.emailRequired && <span style={s.required}>*</span>}</div>
                <input style={s.input} type="email" value={respondentEmail} onChange={e => setRespondentEmail(e.target.value)} placeholder="your@email.com" />
                {errors['_email'] && <div style={s.error}>{errors['_email']}</div>}
              </div>
            )}
          </div>
        )
      )}

      {/* Section header for current page */}
      {currentPage.section && (
        <div style={s.sectionBreak}>
          {currentPage.section.label && <RichText html={currentPage.section.label} style={s.sectionTitle} />}
          {currentPage.section.description && <RichText html={currentPage.section.description} style={{ fontSize: 13, color: '#8d9db6', marginTop: 4 }} />}
        </div>
      )}

      {visibleQuestions.map((q) => {
        if (q.type === 'hyperlink') return (
          <div key={q.id} style={{ ...s.card, borderLeft: '3px solid transparent' }}>
            {q.label && <RichText html={q.label} style={{ fontSize: 15, color: '#1a2b4b', marginBottom: 10, lineHeight: 1.5 }} />}
            {q.linkUrl && (
              <a
                href={q.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: accentColor || 'var(--ka-navy)', fontWeight: 600, fontSize: 15, textDecoration: 'underline', wordBreak: 'break-all' }}
              >
                {q.linkText || q.linkUrl}
              </a>
            )}
          </div>
        );

        return (
          <div key={q.id} style={{ ...s.card, borderLeft: errors[q.id] ? '3px solid #ef4444' : '3px solid transparent' }}>
            <div style={s.qLabel}>
              <RichText html={q.label || 'Untitled Question'} as="span" />
              {q.required && <span style={s.required}>*</span>}
            </div>
            {q.description && <RichText html={q.description} style={s.qDesc} />}

            {q.type === 'short_text' && (
              <input style={s.input} value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}
            {q.type === 'long_text' && (
              <textarea style={s.textarea} value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}
            {q.type === 'number' && (
              <input style={{ ...s.input, width: 160 }} type="number" value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder || '0'} />
            )}
            {q.type === 'date' && (
              <input style={{ ...s.input, width: 200 }} type="date" value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)} />
            )}
            {q.type === 'multiple_choice' && q.options.map(opt => (
              <label key={opt} style={s.radioRow}>
                <input type="radio" name={q.id} value={opt} checked={answers[q.id] === opt} onChange={() => setAnswer(q.id, opt)} style={{ accentColor }} />
                {opt}
              </label>
            ))}
            {q.type === 'checkboxes' && q.options.map(opt => (
              <label key={opt} style={s.radioRow}>
                <input type="checkbox" checked={(answers[q.id] || []).includes(opt)} onChange={() => toggleCheckbox(q.id, opt)} style={{ accentColor }} />
                {opt}
              </label>
            ))}
            {q.type === 'dropdown' && (
              <select style={s.select} value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)}>
                <option value="">— Select an option —</option>
                {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
            {q.type === 'rating' && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={s.ratingRow}>
                    {Array.from({ length: q.ratingMax || 5 }, (_, i) => i + 1).map(n => (
                      <button
                        key={n}
                        style={{
                          ...s.ratingBtn,
                          background: answers[q.id] === n ? accentColor : 'white',
                          borderColor: answers[q.id] === n ? accentColor : '#e2e6ea',
                          color: answers[q.id] === n ? 'white' : '#1a2744',
                        }}
                        onClick={() => setAnswer(q.id, n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {(q.ratingLabels?.low || q.ratingLabels?.high) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 6, fontSize: 11, color: '#8d9db6' }}>
                      <span>{q.ratingLabels.low}</span>
                      <span>{q.ratingLabels.high}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {q.type === 'file_upload' && (
              <FileUploadField
                q={q}
                answer={answers[q.id]}
                uploading={fileUploading[q.id]}
                accentColor={accentColor}
                onUpload={(files) => handleFileUpload(q.id, files, q)}
              />
            )}

            {errors[q.id] && <div style={s.error}>{errors[q.id]}</div>}
          </div>
        );
      })}

      {errors['_submit'] && (
        <div style={{ ...s.error, width: '100%', maxWidth: 640, boxSizing: 'border-box' }}>
          {errors['_submit']}
        </div>
      )}

      {/* Section pagination indicator */}
      {sectionPages.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          {resolvedSectionOrder.map((_, i) => (
            <div key={i} style={{
              width: i === safeCurrentIdx ? 20 : 8,
              height: 8,
              borderRadius: 4,
              background: i === safeCurrentIdx ? accentColor : i < safeCurrentIdx ? accentColor + '88' : '#e2e6ea',
              transition: 'all 0.2s',
            }} />
          ))}
        </div>
      )}

      {isLastPage ? (
        <button
          style={{ ...s.submitBtn, background: accentColor, opacity: submitting ? 0.7 : 1 }}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit Response'}
        </button>
      ) : (
        <button
          style={{ ...s.submitBtn, background: accentColor }}
          onClick={goNextSection}
        >
          Next →
        </button>
      )}

      <div style={s.kaFooter}>
        Powered by King's Academy Faculty Tool Kit · Madaba, Jordan
      </div>
    </div>
  );
}

// ── File upload field (needs its own component so useRef is valid) ──────────
function FileUploadField({ q, answer, uploading, accentColor, onUpload }) {
  const inputRef = React.useRef();
  const uploadedList = answer ? (Array.isArray(answer) ? answer : [answer]) : [];

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        style={s.fileUploadInput}
        accept={q.fileAccept || undefined}
        multiple={q.fileMultiple || false}
        onChange={e => onUpload(e.target.files)}
      />
      <div
        role="button"
        tabIndex={0}
        style={{
          ...s.fileUploadZone,
          borderColor: uploading !== undefined && uploading !== 'error'
            ? accentColor
            : uploadedList.length > 0 ? '#6366f1' : '#e2e6ea',
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.borderColor = accentColor; }}
        onDragLeave={e => { e.currentTarget.style.background = '#fafbfc'; e.currentTarget.style.borderColor = '#e2e6ea'; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.background = '#fafbfc';
          onUpload(e.dataTransfer.files);
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2744', marginBottom: 4 }}>
          {uploading !== undefined && uploading !== 'error'
            ? 'Uploading…'
            : uploadedList.length > 0
            ? 'Click or drag to replace'
            : 'Click or drag to upload'}
        </div>
        <div style={{ fontSize: 12, color: '#8d9db6' }}>
          {q.fileAccept
            ? `Accepted: ${q.fileAccept.replace(/\*/g, 'all')}`
            : 'Any file type'}{' '}
          · Max {q.fileMaxMB || 10} MB
        </div>
      </div>

      {/* Progress bar */}
      {uploading !== undefined && uploading !== 'error' && (
        <div style={s.progressBar}>
          <div style={{ ...s.progressFill, width: `${uploading}%`, background: accentColor }} />
        </div>
      )}

      {/* Uploaded file chips */}
      {uploadedList.map((f, fi) => (
        <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: fi === 0 ? 8 : 4 }}>
          <span style={s.fileChip}>📄 {f.name}</span>
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: accentColor, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            Preview
          </a>
        </div>
      ))}
    </div>
  );
}